import React from 'react';
import { ConnectionStatus } from './ConnectionStatus';
import { FileTextIcon, ClockIcon, ExternalLinkIcon, DatabaseIcon, MessageSquareIcon, BookOpenIcon, FolderIcon } from 'lucide-react';
interface ApiConnection {
  id: string;
  name: string;
  description: string;
  status: 'connected' | 'disconnected';
  lastSync: string;
  type: 'crm' | 'helpdesk' | 'wiki' | 'document';
  documentsCount: number;
}
interface Props {
  connection: ApiConnection;
}
export const ApiConnectionCard = ({
  connection
}: Props) => {
  const getTypeIcon = () => {
    switch (connection.type) {
      case 'crm':
        return <DatabaseIcon size={20} className="text-accent-light" data-id="element-627" />;
      case 'helpdesk':
        return <MessageSquareIcon size={20} className="text-accent-light" data-id="element-628" />;
      case 'wiki':
        return <BookOpenIcon size={20} className="text-accent-light" data-id="element-629" />;
      case 'document':
        return <FolderIcon size={20} className="text-accent-light" data-id="element-630" />;
      default:
        return <ExternalLinkIcon size={20} className="text-gray-400" data-id="element-631" />;
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="bg-surface-light border border-surface-border rounded-none p-6 
                 hover:bg-surface-mid transition-colors group" role="article" aria-label={`${connection.name} API connection - ${connection.status}`} data-id="element-632">
      <div className="flex items-start justify-between" data-id="element-633">
        <div className="flex items-start gap-4" data-id="element-634">
          <div className="p-2 bg-surface rounded-none border border-surface-border group-hover:border-accent/30 transition-colors" data-id="element-635">
            {getTypeIcon()}
          </div>
          <div className="space-y-1" data-id="element-636">
            <div className="flex items-center gap-3" data-id="element-637">
              <h3 className="text-lg font-medium text-white font-mono" data-id="element-638">
                {connection.name}
              </h3>
              <ConnectionStatus status={connection.status} data-id="element-639" />
            </div>
            {connection.description && <p className="text-sm text-gray-400" data-id="element-640">{connection.description}</p>}
            <div className="flex items-center gap-4 text-sm text-gray-400" data-id="element-641">
              <div className="flex items-center gap-1" data-id="element-642">
                <ClockIcon size={14} className="text-accent-light" data-id="element-643" />
                <span data-id="element-644">Last sync: {formatDate(connection.lastSync)}</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-645">
                <FileTextIcon size={14} className="text-accent-light" data-id="element-646" />
                <span data-id="element-647">
                  {connection.documentsCount.toLocaleString()} documents
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-648">
          <button className="text-sm font-medium text-accent hover:text-accent-light transition-colors" aria-label={`View details for ${connection.name}`} data-id="element-649">
            View Details
          </button>
          <span className="text-surface-border" data-id="element-650">|</span>
          <button className="text-sm font-medium text-gray-400 hover:text-white transition-colors" aria-label={connection.status === 'connected' ? `Disconnect ${connection.name}` : `Reconnect ${connection.name}`} data-id="element-651">
            {connection.status === 'connected' ? 'Disconnect' : 'Reconnect'}
          </button>
        </div>
      </div>
      {connection.status === 'connected' && <div className="mt-4 pt-4 border-t border-surface-border" data-id="element-652">
          <div className="flex gap-4" data-id="element-653">
            <button className="text-sm text-accent hover:text-accent-light transition-colors font-medium" aria-label={`Test connection for ${connection.name}`} data-id="element-654">
              Test Connection
            </button>
            <button className="text-sm text-accent hover:text-accent-light transition-colors font-medium" aria-label={`Sync now for ${connection.name}`} data-id="element-655">
              Sync Now
            </button>
            <button className="text-sm text-accent hover:text-accent-light transition-colors font-medium" aria-label={`View logs for ${connection.name}`} data-id="element-656">
              View Logs
            </button>
          </div>
        </div>}
    </div>;
};